import JSZip from "jszip";
import * as XLSX from "xlsx";

/**
 * Converts a 0-based column index to an Excel column letter (0 → "A", 25 → "Z", 26 → "AA").
 *
 * @param {number} index - 0-based column index
 * @returns {string} Excel column letter(s)
 */
export const getColumnName = (index) => {
  let columnName = "";
  let dividend = index + 1;

  while (dividend > 0) {
    const modulo = (dividend - 1) % 26;
    columnName = String.fromCharCode(65 + modulo) + columnName;
    dividend = Math.floor((dividend - modulo) / 26);
  }

  return columnName;
};

/**
 * Reads the first sheet of an xlsx/xls file and returns rows as arrays of cell values.
 *
 * Each row is an array of raw cell values (numbers stay as numbers, strings as strings).
 * Blank rows are skipped. By default every row is returned, including row 0 (no data is dropped).
 *
 * @param {File} file - A browser File object (from <input type="file"> or drag-and-drop)
 * @param {Object} [options]
 * @param {boolean} [options.skipFirstRow=false] - If true, omits the first row (often a header;
 *   the reader does not detect headers—this only slices off index 0).
 * @returns {Promise<Array<Array<*>>>} Array of row arrays
 *
 * @example
 *   const rows = await readFirstSheet(file, { skipFirstRow: true });
 *   // rows[0] = [null, null, "CA - Los Angeles", "Tornado - Low", 500000, ...]
 *
 * @example
 *   const allRows = await readFirstSheet(file);
 *   // allRows[0] = ["State", "Address", "Description", ...] (first/header row)
 */
export const readFirstSheet = async (file, { skipFirstRow = false } = {}) => {
  const fileBytes = await file.arrayBuffer();
  const workbook = XLSX.read(fileBytes, { type: "array" });
  const firstSheet = workbook.SheetNames?.[0];
  if (!firstSheet) return [];
  const rows = XLSX.utils.sheet_to_json(workbook.Sheets[firstSheet], {
    header: 1,
    raw: true,
    defval: null,
    blankrows: false,
  });
  return skipFirstRow ? rows.slice(1) : rows;
};

/**
 * Generates and downloads an xlsx template file with optional in-cell dropdown validation.
 *
 * Builds a workbook with one or more data sheets and an optional reference sheet containing
 * dropdown list values. When `validations` are provided, injects Excel data-validation XML
 * so that cells in the first sheet show native dropdown arrows in Excel/Google Sheets.
 *
 * Uses OOXML XML patching via JSZip because the xlsx open-source library does not support
 * the `!dataValidations` worksheet property.
 *
 * @param {Object} config
 *
 * @param {string} config.filename
 *   Download filename including extension (e.g. "Cat_Exposure_Template.xlsx").
 *
 * @param {Array<SheetDef>} config.sheets
 *   One or more sheet definitions. The first sheet is the one that receives data validations.
 *   @typedef {Object} SheetDef
 *   @property {string} name - Tab name shown in Excel (e.g. "Data Entry")
 *   @property {string[]} headers - Column header labels for row 1
 *   @property {number} [dataRows=200] - Number of empty rows below the header (for user input)
 *   @property {number[]} [colWidths] - Column widths (in characters). If omitted, auto-sized
 *     from header text length.
 *
 * @param {Array<DropdownDef>} [config.dropdowns]
 *   Dropdown list definitions. Each becomes a column in the reference sheet.
 *   @typedef {Object} DropdownDef
 *   @property {string} header - Column header in the reference sheet (e.g. "Region/Peril")
 *   @property {string[]} values - Valid dropdown choices (display names)
 *
 * @param {string} [config.dropdownSheetName="DropDowns"]
 *   Name of the reference sheet tab that holds the dropdown values.
 *
 * @param {Array<ValidationDef>} [config.validations]
 *   Maps data columns on the first sheet to dropdown lists for in-cell validation.
 *   @typedef {Object} ValidationDef
 *   @property {string} dataColumn - Excel column letter on the first sheet (e.g. "D", "F")
 *   @property {number} dropdownIndex - 0-based index into the `dropdowns` array
 *
 * @returns {Promise<void>} Resolves after the file download is triggered
 *
 * @example
 *   // Simple template with no dropdowns
 *   await generateExcelTemplate({
 *     filename: "Import_Template.xlsx",
 *     sheets: [{ name: "Data", headers: ["Name", "Amount", "Date"] }],
 *   });
 *
 * @example
 *   // Template with dropdown validations on columns B and D
 *   await generateExcelTemplate({
 *     filename: "Cat_Exposure_Template.xlsx",
 *     sheets: [{
 *       name: "Manual Cat Exposure",
 *       headers: ["State", "Address", "Description", "Region/Peril", "Value", "Cat Hazard"],
 *       dataRows: 300,
 *     }],
 *     dropdowns: [
 *       { header: "Region/Peril", values: ["Tornado - Low", "Tornado - High", "Flood - Low"] },
 *       { header: "Cat Hazard", values: ["Ancillary only (0.2)", "Minimal (0.8)"] },
 *     ],
 *     validations: [
 *       { dataColumn: "D", dropdownIndex: 0 },
 *       { dataColumn: "F", dropdownIndex: 1 },
 *     ],
 *   });
 */
export const generateExcelTemplate = async ({
  filename,
  sheets,
  dropdowns,
  dropdownSheetName = "DropDowns",
  validations,
}) => {
  const wb = XLSX.utils.book_new();

  sheets.forEach(({ name, headers, dataRows = 200, colWidths }) => {
    const wsData = [headers];
    for (let i = 0; i < dataRows; i++) wsData.push([]);
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws["!cols"] = colWidths
      ? colWidths.map((w) => ({ wch: w }))
      : headers.map((h) => ({ wch: Math.max(h.length + 4, 22) }));
    XLSX.utils.book_append_sheet(wb, ws, name);
  });

  if (dropdowns?.length) {
    const maxLen = Math.max(...dropdowns.map((d) => d.values.length));
    const dropdownData = [dropdowns.map((d) => d.header)];
    for (let i = 0; i < maxLen; i++) {
      dropdownData.push(dropdowns.map((d) => d.values[i] ?? null));
    }
    const wsDropdowns = XLSX.utils.aoa_to_sheet(dropdownData);
    wsDropdowns["!cols"] = dropdowns.map((d) => ({
      wch: Math.max(d.header.length + 4, 25),
    }));
    XLSX.utils.book_append_sheet(wb, wsDropdowns, dropdownSheetName);
  }

  if (!validations?.length || !dropdowns?.length) {
    XLSX.writeFile(wb, filename);
    return;
  }

  // Inject data validations via XML patching (not supported in xlsx open-source)
  const dataRows = sheets[0]?.dataRows ?? 200;
  const endRow = dataRows + 1;
  const validationEntries = validations.map(({ dataColumn, dropdownIndex }) => {
    const listCol = getColumnName(dropdownIndex);
    const listLen = dropdowns[dropdownIndex].values.length;
    return (
      `<dataValidation type="list" showDropDown="0" showErrorMessage="1" errorStyle="warning" ` +
      `sqref="${dataColumn}2:${dataColumn}${endRow}">` +
      `<formula1>${dropdownSheetName}!$${listCol}$2:$${listCol}$${
        listLen + 1
      }</formula1>` +
      `</dataValidation>`
    );
  });
  const validationsXml = `<dataValidations count="${
    validationEntries.length
  }">${validationEntries.join("")}</dataValidations>`;

  const bytes = XLSX.write(wb, { type: "array", bookType: "xlsx" });

  const zip = await JSZip.loadAsync(bytes);
  const sheetXml = await zip.files["xl/worksheets/sheet1.xml"].async("string");
  const insertBefore = sheetXml.includes("<ignoredErrors>")
    ? "<ignoredErrors>"
    : "</worksheet>";
  const patchedXml = sheetXml.replace(
    insertBefore,
    `${validationsXml}${insertBefore}`
  );
  zip.file("xl/worksheets/sheet1.xml", patchedXml);

  const blob = await zip.generateAsync({
    type: "blob",
    mimeType:
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};
